import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardActionArea,
  Typography,
  IconButton,
  Collapse,
  Tabs,
  Tab,
} from "@mhome/ui";
import { CircularProgress } from "@mui/material";
import { spacingToPx } from "../lib/utils";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Delete as DeleteIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import ErrorIcon from "@mui/icons-material/Error";
import { useRecoilValue } from "recoil";
import { wsReadyState } from "../global-state";
import { useComponentBackgroundColor } from "../common/adaptive-theme-provider";
import {
  listQuickWords,
  listSuggestedQuickWords,
  listStandardQuickWordAutomations,
} from "../service/quick-word-service";
import ResponsiveCardGrid from "../common/responsive-card-grid";
import MSwitch from "../common/switch";
import dayjs from "dayjs";
import { useCustomFormat } from "../views/after-init";
import QuickWordsFilter from "./quick-words-filter";
import { useHistory } from "react-router-dom";
import {
  enableAutomation,
  disableAutomation,
  deleteAutomation,
} from "../service/automation-service";
import { useToast } from "../common/toast";
import { useWebSocket } from "../ws/websocket";

const QuickWordsView = ({ showFilter = false, setShowFilter }) => {
  const { sendAndWaitWs } = useWebSocket();
  const history = useHistory();
  const { successToast, errorToast } = useToast();
  const customFormat = useCustomFormat();
  const getComponentBgColor = useComponentBackgroundColor();
  const [quickWords, setQuickWords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedQuickWords, setExpandedQuickWords] = useState({});
  const [quickWordAutomations, setQuickWordAutomations] = useState({});
  const [loadingAutomations, setLoadingAutomations] = useState({});
  const [currentTime, setCurrentTime] = useState(dayjs());
  const [filterText, setFilterText] = useState("");
  const [sortBy, setSortBy] = useState("a-z");
  const [activeTab, setActiveTab] = useState(0);
  const wsReady = useRecoilValue(wsReadyState);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(dayjs());
    }, 60000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (wsReady) {
      setShowFilter(false);
      setFilterText("");
      fetchQuickWords();
    }
  }, [wsReady]);

  const fetchQuickWords = async () => {
    setLoading(true);
    try {
      // Call both APIs in parallel
      const [quickWordResp, suggestedQuickWordResp] = await Promise.all([
        listQuickWords(sendAndWaitWs),
        listSuggestedQuickWords(sendAndWaitWs),
      ]);

      // Process quick words with source information
      const quickWordsData = Array.isArray(quickWordResp.quickWords)
        ? quickWordResp.quickWords.map((w) => ({
            text: w.quickWord,
            source: "quickword",
            type: "regular",
          }))
        : [];

      // Process suggested quick words with new API format (includes automationId)
      const suggestedQuickWordsData = Array.isArray(
        suggestedQuickWordResp?.quickWords
      )
        ? suggestedQuickWordResp.quickWords.map((w) => ({
            text: w.quickWord,
            automationId: w.automationId,
            source: "quickword",
            type: "suggested",
          }))
        : [];

      // Combine both lists (quick words first, then suggested)
      const combinedQuickWords = [
        ...quickWordsData,
        ...suggestedQuickWordsData,
      ];
      setQuickWords(combinedQuickWords);
    } catch (error) {
      console.error("Failed to fetch quick words:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuickWordAutomations = async (quickWord) => {
    const quickWordText = quickWord.text;
    setLoadingAutomations((prev) => ({ ...prev, [quickWordText]: true }));
    try {
      // Only fetch for regular quick words - suggested ones have automationId directly
      if (quickWord.type === "regular") {
        const response = await listStandardQuickWordAutomations(
          sendAndWaitWs,
          quickWordText
        );

        if (response && Array.isArray(response.automations)) {
          setQuickWordAutomations((prev) => ({
            ...prev,
            [quickWordText]: response.automations,
          }));
        } else {
          setQuickWordAutomations((prev) => ({
            ...prev,
            [quickWordText]: [],
          }));
        }
      }
    } catch (error) {
      console.error("Failed to fetch automations for quick word:", error);
      setQuickWordAutomations((prev) => ({
        ...prev,
        [quickWordText]: [],
      }));
    } finally {
      setLoadingAutomations((prev) => ({ ...prev, [quickWordText]: false }));
    }
  };

  const handleToggleExpand = (quickWord) => {
    // Only handle expand/collapse for regular quick words
    if (quickWord.type !== "regular") return;

    const quickWordText = quickWord.text;
    const isExpanded = expandedQuickWords[quickWordText];

    if (!isExpanded) {
      // Expanding - fetch automations if not already fetched
      if (!quickWordAutomations[quickWordText]) {
        fetchQuickWordAutomations(quickWord);
      }
    }

    setExpandedQuickWords((prev) => ({
      ...prev,
      [quickWordText]: !isExpanded,
    }));
  };

  const handleEnableDisable = async (
    event,
    enableDisable,
    automationId,
    quickWordText
  ) => {
    event.stopPropagation();

    if (enableDisable) {
      const response = await enableAutomation(sendAndWaitWs, automationId);
      if (response && !response.error) {
        successToast("Enabled");
        // Update the automation in the local state
        setQuickWordAutomations((prev) => ({
          ...prev,
          [quickWordText]: prev[quickWordText].map((automation) =>
            automation.id === automationId
              ? { ...automation, enabled: response.enabled }
              : automation
          ),
        }));
      } else {
        errorToast("Error!");
      }
    } else {
      const response = await disableAutomation(sendAndWaitWs, automationId);
      if (response && !response.error) {
        successToast("Disabled");
        // Update the automation in the local state
        setQuickWordAutomations((prev) => ({
          ...prev,
          [quickWordText]: prev[quickWordText].map((automation) =>
            automation.id === automationId
              ? { ...automation, enabled: response.enabled }
              : automation
          ),
        }));
      } else {
        errorToast("Error!");
      }
    }
  };

  const handleDeleteAutomation = async (
    event,
    automationId,
    quickWordText,
    force = false
  ) => {
    event.stopPropagation();
    const success = await deleteAutomation(sendAndWaitWs, automationId, force);
    if (success) {
      successToast("Deleted.");

      // Find the quick word to determine its type
      const quickWord = quickWords.find((qw) => qw.text === quickWordText);

      if (quickWord?.type === "suggested") {
        // For suggested quick words, remove the entire quick word from the list
        setQuickWords((prev) =>
          prev.filter(
            (qw) =>
              !(qw.text === quickWordText && qw.automationId === automationId)
          )
        );
      } else {
        // For regular quick words, remove just the automation from the automation list
        setQuickWordAutomations((prev) => ({
          ...prev,
          [quickWordText]:
            prev[quickWordText]?.filter(
              (automation) => automation.id !== automationId
            ) || [],
        }));
      }
    } else {
      errorToast("Error!");
    }
  };

  const openAutomationOverview = (automationId) => {
    history.push(`/automation/overview?id=${automationId}`);
  };

  // Filter and sort quick words
  const filteredQuickWords = quickWords
    .filter((qw) => qw.text.toLowerCase().includes(filterText.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === "a-z") {
        return a.text.toLowerCase().localeCompare(b.text.toLowerCase());
      } else if (sortBy === "z-a") {
        return b.text.toLowerCase().localeCompare(a.text.toLowerCase());
      }
      return 0;
    });

  const renderAutomationCard = (automation, quickWordText, quickWordType) => (
    <Card
      key={automation.id}
      elevation={0}
      className="mb-1"
      style={{
        border: `1px solid hsl(var(--border))`,
        borderRadius: "16px",
        backgroundColor: getComponentBgColor("hsl(var(--card))"),
        boxShadow: "none",
      }}
    >
      <CardActionArea
        onClick={() => openAutomationOverview(automation.id)}
        style={{
          position: "relative",
        }}
      >
        <div style={{ padding: "6px 8px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              gutterBottom
              variant="body1"
              component="div"
              className="overflow-hidden text-ellipsis"
              style={{
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 3,
                maxHeight: "4.5em",
                marginBottom: "0px",
              }}
            >
              {automation.name}
            </Typography>
            {!automation.runnable ? (
              <ErrorIcon
                style={{
                  color: "red",
                  marginRight: "15px",
                  marginLeft: "15px",
                }}
              />
            ) : quickWordType === "suggested" ? (
              <IconButton
                onClick={(e) =>
                  handleDeleteAutomation(e, automation.id, quickWordText)
                }
              >
                <DeleteIcon color="error" />
              </IconButton>
            ) : (
              <MSwitch
                checked={automation.enabled}
                disabled={!automation.runnable}
                onClick={(e) => e.stopPropagation()}
                onChange={(e, b) =>
                  handleEnableDisable(e, b, automation.id, quickWordText)
                }
                inputProps={{ "aria-label": "controlled" }}
              />
            )}
          </div>
          {automation.timestamp !== 0 ? (
            <div>
              <Typography
                variant="caption"
                component="div"
                className="overflow-hidden text-ellipsis"
                style={{
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 3,
                  marginTop: "5px",
                  color: "hsl(var(--muted-foreground))",
                }}
              >
                {customFormat(dayjs(automation.timestamp), currentTime)}
              </Typography>
            </div>
          ) : (
            <div>
              <Typography
                variant="caption"
                component="div"
                className="overflow-hidden text-ellipsis"
                style={{
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 3,
                  marginTop: "5px",
                  color: "hsl(var(--muted-foreground))",
                }}
              >
                Not executed
              </Typography>
            </div>
          )}
        </div>
      </CardActionArea>
    </Card>
  );

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          padding: spacingToPx(3),
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  if (filteredQuickWords.length === 0 && !filterText) {
    return (
      <div
        style={{
          flexGrow: 1,
          margin: spacingToPx(2),
          paddingBottom: spacingToPx(12),
        }}
      >
        {showFilter && (
          <QuickWordsFilter
            sortBy={sortBy}
            setSortBy={setSortBy}
            setFilterText={setFilterText}
            setShowFilter={setShowFilter}
          />
        )}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            width: "100%",
            padding: spacingToPx(3),
          }}
        >
          <Typography
            variant="h6"
            color="textSecondary"
            style={{ marginBottom: spacingToPx(1) }}
          >
            No quick commands
          </Typography>
        </div>
      </div>
    );
  }

  // Separate filtered quick words into categories
  const standardQuickWords = filteredQuickWords.filter(
    (qw) => qw.type === "regular"
  );
  const suggestedQuickWords = filteredQuickWords.filter(
    (qw) => qw.type === "suggested"
  );

  return (
    <div
      style={{
        flexGrow: 1,
        marginLeft: spacingToPx(2),
        marginRight: spacingToPx(2),
        marginTop: spacingToPx(1),
        paddingBottom: spacingToPx(12),
      }}
    >
      {showFilter && (
        <QuickWordsFilter
          sortBy={sortBy}
          setSortBy={setSortBy}
          setFilterText={setFilterText}
          setShowFilter={setShowFilter}
        />
      )}

      {filteredQuickWords.length === 0 ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            width: "100%",
            padding: spacingToPx(2),
          }}
        >
          <Typography
            variant="h6"
            color="textSecondary"
            style={{ marginBottom: spacingToPx(1) }}
          >
            No quick commands match your filter
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Try adjusting your search criteria
          </Typography>
        </div>
      ) : (
        <>
          {/* Tab Navigation */}
          <div
            style={{
              borderBottom: `1px solid hsl(var(--border))`,
              marginBottom: spacingToPx(1),
            }}
          >
            <Tabs
              value={activeTab}
              onChange={(event, newValue) => setActiveTab(newValue)}
              variant="fullWidth"
              style={
                {
                  // Tab styles handled by Tabs component
                }
              }
              className="[&_.MuiTab-root]:normal-case [&_.MuiTab-root]:text-base [&_.MuiTab-root]:font-medium"
            >
              <Tab
                label={`Manual Quick Commands (${standardQuickWords.length})`}
              />
              <Tab
                label={`Assistant Auto-generated (${suggestedQuickWords.length})`}
              />
            </Tabs>
          </div>

          {/* Tab Content */}
          {activeTab === 0 &&
            (standardQuickWords.length > 0 ? (
              <ResponsiveCardGrid
                spacing={2}
                breakpoints={{ xs: 1, sm: 2, md: 3 }}
              >
                {standardQuickWords.map((quickWord, index) => (
                  <Card
                    elevation={0}
                    key={index}
                    style={{
                      backgroundColor: getComponentBgColor("hsl(var(--card))"),
                    }}
                  >
                    <CardActionArea
                      onClick={() => handleToggleExpand(quickWord)}
                      className="relative h-full flex flex-col items-stretch justify-start"
                    >
                      <CardContent
                        className="h-full flex flex-col"
                        style={{
                          padding: "8px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                          }}
                        >
                          <Typography
                            gutterBottom
                            variant="body1"
                            component="div"
                            className="overflow-hidden text-ellipsis"
                            style={{
                              display: "-webkit-box",
                              WebkitBoxOrient: "vertical",
                              WebkitLineClamp: 3,
                              maxHeight: "4.5em",
                              marginBottom: "0px",
                            }}
                          >
                            {quickWord.text}
                          </Typography>
                          <IconButton
                            size="small"
                            style={{ marginLeft: spacingToPx(1) }}
                          >
                            {expandedQuickWords[quickWord.text] ? (
                              <ExpandLessIcon />
                            ) : (
                              <ExpandMoreIcon />
                            )}
                          </IconButton>
                        </div>

                        <Collapse
                          in={expandedQuickWords[quickWord.text]}
                          timeout="auto"
                          unmountOnExit
                        >
                          <div style={{ marginTop: spacingToPx(1) }}>
                            {loadingAutomations[quickWord.text] ? (
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "center",
                                  padding: spacingToPx(1),
                                }}
                              >
                                <CircularProgress size={20} />
                              </div>
                            ) : (
                              <div>
                                {quickWordAutomations[quickWord.text]?.length >
                                0 ? (
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: spacingToPx(0.5),
                                    }}
                                  >
                                    {quickWordAutomations[quickWord.text].map(
                                      (automation) =>
                                        renderAutomationCard(
                                          automation,
                                          quickWord.text,
                                          quickWord.type
                                        )
                                    )}
                                  </div>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    style={{
                                      paddingTop: spacingToPx(1),
                                      paddingBottom: spacingToPx(1),
                                    }}
                                  >
                                    No related automations found
                                  </Typography>
                                )}
                              </div>
                            )}
                          </div>
                        </Collapse>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                ))}
              </ResponsiveCardGrid>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                  width: "100%",
                  padding: spacingToPx(3),
                }}
              >
                <Typography variant="body1" color="textSecondary">
                  No manual quick commands found
                </Typography>
              </div>
            ))}

          {activeTab === 1 &&
            (suggestedQuickWords.length > 0 ? (
              <ResponsiveCardGrid
                spacing={2}
                breakpoints={{ xs: 1, sm: 2, md: 3 }}
              >
                {suggestedQuickWords.map((quickWord, index) => (
                  <Card
                    elevation={0}
                    key={index}
                    style={{
                      backgroundColor: getComponentBgColor("hsl(var(--card))"),
                    }}
                  >
                    <CardActionArea
                      onClick={() =>
                        openAutomationOverview(quickWord.automationId)
                      }
                      className="relative h-full flex flex-col items-stretch justify-start"
                    >
                      <CardContent
                        className="h-full flex flex-col"
                        style={{
                          padding: "8px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                          }}
                        >
                          <Typography
                            gutterBottom
                            variant="body1"
                            component="div"
                            className="overflow-hidden text-ellipsis"
                            style={{
                              display: "-webkit-box",
                              WebkitBoxOrient: "vertical",
                              WebkitLineClamp: 3,
                              maxHeight: "4.5em",
                              marginBottom: "0px",
                            }}
                          >
                            {quickWord.text}
                          </Typography>
                          <div style={{ display: "flex", gap: "4px" }}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteAutomation(
                                  e,
                                  quickWord.automationId,
                                  quickWord.text,
                                  true
                                );
                              }}
                              className="text-destructive"
                            >
                              <DeleteIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                openAutomationOverview(quickWord.automationId);
                              }}
                              className="text-primary"
                            >
                              <ArrowForwardIcon />
                            </IconButton>
                          </div>
                        </div>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                ))}
              </ResponsiveCardGrid>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                  width: "100%",
                  padding: spacingToPx(3),
                }}
              >
                <Typography variant="body1" color="textSecondary">
                  No assistant auto-generated quick commands found
                </Typography>
              </div>
            ))}
        </>
      )}
    </div>
  );
};

export default QuickWordsView;
